import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { gettext } from '@eb/i18n';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import { PlusChunky } from '@eb/eds-iconography';

import { Icon } from '@eb/eds-icon';
import { Button } from '@eb/eds-button';
import { STYLE_LINK, TYPE_SUBMIT, SIZE_BLOCK } from '@eb/eds-button';
import { Divider } from '@eb/eds-divider';
import { InputField } from '@eb/eds-input-field';

import { CheckboxGroup } from '@eb/eds-checkbox-group';
import { Checkbox } from '@eb/eds-checkbox';

import './collectionView.scss';

// eslint-disable-next-line react/no-multi-comp
export class AddToCollectionView extends PureComponent {
    static propTypes = {
        onSaveEvent: PropTypes.func.isRequired,
        userCollections: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                id: PropTypes.string,
                href: PropTypes.string,
                eventCount: PropTypes.number,
            }),
        ).isRequired,
        eventCollections: PropTypes.arrayOf(PropTypes.string),
        isSmallScreen: PropTypes.bool,
        onAddOrRemoveEventToCollection: PropTypes.func,
        onCloseDropdown: PropTypes.func,
        onShowCreateCollection: PropTypes.func,
        savedByYou: PropTypes.bool,
        eventId: PropTypes.string,
    };

    render() {
        const {
            userCollections,
            eventCollections,
            isSmallScreen,
            onAddOrRemoveEventToCollection,
            onCloseDropdown,
            onShowCreateCollection,
            savedByYou,
            onSaveEvent,
            eventId,
        } = this.props;

        const checkboxContainerClassnames = classNames(
            'add-to-collection__main',
            'eds-l-mar-hor-6',
            {
                'add-to-collection__main--desktop eds-l-pad-bot-2': !isSmallScreen,
                'eds-l-pad-bot-8': isSmallScreen,
            },
        );
        const bottomButtonContainerClassName = classNames({
            'eds-align--center eds-l-mar-top-2': !isSmallScreen,
            'collection-button__smallscreen eds-l-pad-all-4': isSmallScreen,
        });

        return (
            <div className="add-to-collection">
                <div className="eds-l-pad-left-2 eds-l-pad-bot-1">
                    <Button style={STYLE_LINK} onClick={onShowCreateCollection}>
                        <Icon type={<PlusChunky />} color="ui-blue" />
                        {gettext('New collection')}
                    </Button>
                </div>
                <div className={checkboxContainerClassnames}>
                    <Checkbox
                        value={savedByYou}
                        id="liked-checkbox"
                        onChange={onSaveEvent.bind(null, eventId)}
                    >
                        {gettext('Likes')}
                    </Checkbox>
                    <CheckboxGroup
                        values={userCollections.map(({ name, id }) => ({
                            display: name,
                            value: id,
                        }))}
                        value={eventCollections}
                        name="user-collection-group"
                        onChange={onAddOrRemoveEventToCollection}
                    />
                </div>
                <div className="eds-show-up-md">
                    <Divider />
                </div>
                <div className={bottomButtonContainerClassName}>
                    <Button
                        style={STYLE_LINK}
                        size={SIZE_BLOCK}
                        onClick={onCloseDropdown}
                    >
                        {gettext('Done')}
                    </Button>
                </div>
            </div>
        );
    }
}

// eslint-disable-next-line react/no-multi-comp
export class CreateCollectionView extends PureComponent {
    static propTypes = {
        onCreateCollection: PropTypes.func.isRequired,
        isSmallScreen: PropTypes.bool,
    };

    state = {
        inputValue: '',
        errorNote: '',
    };

    _handleInputChange = (val) => {
        this.setState({ inputValue: val });
    };

    _handleSubmit = (e) => {
        const { onCreateCollection } = this.props;

        e.preventDefault();

        if (this.state.inputValue) {
            onCreateCollection(this.state.inputValue);
        } else {
            this.setState({ errorNote: gettext('Name is required') });
        }
    };

    _handleFocus = () => {
        this.setState({ errorNote: '' });
    };

    render() {
        const { inputValue, errorNote } = this.state;
        const { isSmallScreen } = this.props;

        const bottomButtonContainerClassName = classNames(
            'eds-align--center eds-l-mar-top-6 eds-l-mar-bot-4',
            {
                'collection-button__smallscreen eds-l-mar-hor-4': isSmallScreen,
            },
        );

        return (
            <form onSubmit={this._handleSubmit} className="eds-l-pad-hor-4">
                <InputField
                    id="collection-name"
                    onChange={this._handleInputChange}
                    onFocus={this._handleFocus}
                    value={inputValue}
                    label={gettext('Name')}
                    placeholder={gettext('e.g. "New Years Parties"')}
                    required={true}
                    hasError={!!errorNote}
                    annotationNote={errorNote}
                />
                <div className={bottomButtonContainerClassName}>
                    <Button
                        style={STYLE_LINK}
                        type={TYPE_SUBMIT}
                        size={SIZE_BLOCK}
                    >
                        {gettext('Create')}
                    </Button>
                </div>
            </form>
        );
    }
}

/*
Container view to be passed into the
dropdown/modal in the eventcard
logic when the icon is clicked.

The view will manage the state to
 control when to show/hide create
 flow vs add or remove flow
*/

// eslint-disable-next-line react/no-multi-comp
export default class CollectionView extends PureComponent {
    static propTypes = {
        userCollections: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                id: PropTypes.string,
                href: PropTypes.string,
                eventCount: PropTypes.number,
            }),
        ).isRequired,
        isSmallScreen: PropTypes.bool,
        onCreateCollection: PropTypes.func.isRequired,
        onAddOrRemoveEventToCollection: PropTypes.func.isRequired,
        onCloseDropdown: PropTypes.func,
        onSaveEvent: PropTypes.func.isRequired,
        eventId: PropTypes.string,
        eventCollections: PropTypes.arrayOf(PropTypes.string),
        savedByYou: PropTypes.bool,
        onShowCreateCollectionView: PropTypes.func,
    };

    static defaultProps = {
        eventCollections: [],
    };

    state = {
        isCreateCollectionView: false,
    };

    _handleAddOrRemove = (arrayOfCheckedOptions, { isChecked, value }) => {
        const { onAddOrRemoveEventToCollection, eventId } = this.props;

        onAddOrRemoveEventToCollection({
            eventId,
            isChecked,
            value,
        });
    };

    _handleCreateCollection = (name) => {
        const { eventId, onCreateCollection } = this.props;

        onCreateCollection(name, [eventId]);

        this.setState({
            isCreateCollectionView: false,
        });
    };

    _handleShowCreateCollection = () => {
        const { onShowCreateCollectionView } = this.props;

        this.setState(
            {
                isCreateCollectionView: true,
            },
            () => {
                if (onShowCreateCollectionView) {
                    onShowCreateCollectionView();
                }
            },
        );
    };

    // Container view to be passed into the modal
    // in the eventcard logic when the icon
    // is clicked. Will likely need to merge with
    // fiona's work once EB-103695 concludes
    render() {
        const {
            eventCollections,
            userCollections,
            savedByYou,
            eventId,
            isSmallScreen,
            onCloseDropdown,
            onSaveEvent,
        } = this.props;

        const { isCreateCollectionView } = this.state;

        const containerClasses = classNames('eds-l-mar-top-6 eds-l-mar-bot-2', {
            'collection-view collection-view--dropdown': !isSmallScreen,
        });

        const headerClasses = classNames('eds-align--center', {
            'eds-text-bl eds-text-weight--heavy': !isSmallScreen,
            'eds-text-hs': isSmallScreen,
        });

        let content = (
            <AddToCollectionView
                eventCollections={eventCollections}
                userCollections={userCollections}
                onAddOrRemoveEventToCollection={this._handleAddOrRemove}
                onShowCreateCollection={this._handleShowCreateCollection}
                savedByYou={savedByYou}
                onSaveEvent={onSaveEvent}
                onCloseDropdown={onCloseDropdown}
                eventId={eventId}
                isSmallScreen={isSmallScreen}
            />
        );

        let title = gettext('Add to collection');

        let subtitle = null;

        if (isEmpty(userCollections)) {
            title = gettext('Add event to a collection');
            subtitle = (
                <div className="add-to-collection__subtitle eds-text--center eds-text-bs eds-l-mar-top-2">
                    {gettext(
                        "Make plans, share them with friends, and always know what you're doing next",
                    )}
                </div>
            );
        }

        if (isCreateCollectionView) {
            title = gettext('Create a new collection');
            subtitle = null;
            content = (
                <CreateCollectionView
                    isSmallScreen={isSmallScreen}
                    onCreateCollection={this._handleCreateCollection}
                />
            );
        }

        return (
            <div className={containerClasses}>
                <header className="collection-view__header eds-l-pad-bot-2 eds-l-pad-hor-2">
                    <h2 className={headerClasses}>{title}</h2>
                    {subtitle}
                </header>
                {content}
            </div>
        );
    }
}
